import {captureException} from '@sentry/browser';
import {showNotification} from 'platform/components';

import {useState} from 'react';

import {isPlainObj} from 'ramda-adjunct';

import {
  useFileStorageUploadV2Mutation,
  useReplaceInspectionEquipmentDocumentMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, uploadFileWithProgress} from 'shared/web';

export type EquipmentDocumentUploadPhase = 'idle' | 'uploading' | 'attaching';

/**
 * Upload → attach, the same car-audit storage flow inspection photos use: request a signed upload
 * URL, send the raw bytes to it, then attach the returned `uploadedAssetId` together with the
 * author's original file name (the backend serves the download under that name).
 *
 * The displayed attachment is only replaced by the cache invalidation after a successful attach,
 * so a failure anywhere in the chain leaves the previous attachment untouched. Resolves to whether
 * the document was attached; failures are reported here and never reject.
 */
export function useEquipmentDocumentUpload(auditId: string) {
  const [createUpload] = useFileStorageUploadV2Mutation();
  const [replaceEquipmentDocument] = useReplaceInspectionEquipmentDocumentMutation();
  const [phase, setPhase] = useState<EquipmentDocumentUploadPhase>('idle');

  const uploadEquipmentDocument = (file: File) => {
    setPhase('uploading');

    return createUpload()
      .unwrap()
      .then((upload) =>
        uploadFileWithProgress({
          file,
          url: upload.url,
          shouldOmitSharedHeaders: true,
        }).then(
          () => upload.uploadedAssetId,
          () => Promise.reject(new StorageUploadError())
        )
      )
      .then((uploadedAssetId) => {
        setPhase('attaching');
        return replaceEquipmentDocument({
          auditId,
          body: {fileId: uploadedAssetId, fileName: file.name},
        }).unwrap();
      })
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        return true;
      })
      .catch((error: unknown) => {
        reportUploadFailure(error);
        return false;
      })
      .finally(() => setPhase('idle'));
  };

  return {uploadEquipmentDocument, phase};
}

/** The bytes did not reach the signed storage URL. */
class StorageUploadError extends Error {}

/** RTK Query rejects `unwrap()` with plain objects (`FetchBaseQueryError` / `SerializedError`). */
const isApiError = (error: unknown): error is ApiError => isPlainObj(error);

function reportUploadFailure(error: unknown) {
  if (isApiError(error)) {
    handleApiError(error);
    return;
  }

  // Anything other than a failed storage upload is a bug in the chain, not a user-facing failure.
  if (!(error instanceof StorageUploadError)) {
    captureException(error);
  }

  showNotification.error(i18n.t('entity.inspection.errors.equipmentDocumentProcessingFailed'));
}
